import { useState } from 'react'
import { X } from 'lucide-react'
import { useTranslation } from '@/core'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@/ui'
import { useUserDataValue } from '@/features/users'

interface DailyDocumentUploadDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  agendaDate: string
  onSubmit: (files: Array<File>) => Promise<void>
  isPending: boolean
}

export const DailyDocumentUploadDialog = ({
  open,
  onOpenChange,
  agendaDate,
  onSubmit,
  isPending,
}: DailyDocumentUploadDialogProps) => {
  const [files, setFiles] = useState<Array<File>>([])

  const { t } = useTranslation()
  const userData = useUserDataValue()
  const isAdmin = userData?.role === 'admin'

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files)
      setFiles((prev) => [...prev, ...newFiles])
      e.target.value = ''
    }
  }

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (files.length === 0 || !isAdmin) return

    try {
      await onSubmit(files)
      onOpenChange(false)
      setFiles([])
    } catch {
      // The PageGenerator mutation surfaces the request error.
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Carica Documenti della Giornata</DialogTitle>
          <DialogDescription>
            Carica file PDF o Excel per la giornata del {agendaDate}. Questi
            documenti saranno visibili solo agli amministratori.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="daily-files">{t('documents.files')}</Label>
            <Input
              id="daily-files"
              type="file"
              onChange={handleFileChange}
              multiple
              accept=".pdf,.xls,.xlsx,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            />
            {files.length > 0 && (
              <div className="space-y-2 mt-2">
                <p className="text-sm text-muted-foreground">
                  {t('documents.filesSelected', { count: files.length })}
                </p>
                <div className="flex flex-wrap gap-2">
                  {files.map((file, index) => (
                    <div
                      key={`${file.name}-${index}`}
                      className="bg-secondary text-secondary-foreground px-2 py-1 rounded-md text-sm flex items-center gap-1"
                    >
                      <span className="max-w-[150px] truncate">
                        {file.name}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-4 w-4 p-0 hover:bg-destructive/10 hover:text-destructive"
                        onClick={() => handleRemoveFile(index)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="submit" disabled={isPending || files.length === 0}>
              {isPending ? t('common.uploading') : t('documents.uploadButton')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
